// Pure classifier for the Home box (#1527). People read the box as a chat, so what they type is a
// question, an idea (work to build) or unclear. No I/O: the service decides what to do with the kind.

export type HomeBoxKind = "question" | "idea" | "unclear";

export type HomeBoxClassification = { kind: HomeBoxKind; reason: string };

/** An answer built from the office's own state, with links the box can render. */
export type HomeBoxAnswerDTO = {
  markdown: string;
  links: { label: string; href: string }[];
  generatedAt: number;
};

// Words that open a question or a request for information (English, Hebrew).
const QUESTION_STARTS = new Set([
  "what", "whats", "where", "wheres", "why", "how", "when", "who", "whom", "whose", "which",
  "is", "are", "was", "were", "do", "does", "did", "has", "have", "will", "should", "can", "could",
  "status", "explain", "summarize", "summarise", "summary",
  "איפה", "היכן", "מה", "מתי", "למה", "מדוע", "מי", "איך", "כיצד", "כמה", "האם", "איזה", "איזו", "אילו", "מהו", "מהי",
  "סטטוס", "מצב", "תסביר", "סיכום",
]);
// Verbs that are work ("update the README") unless they are addressed to the reader ("update me").
const REQUEST_FOR_INFO = /^(update|tell|show|give|list) (me|us)\b/;

// Verbs that open a piece of work: an idea.
const IDEA_STARTS = new Set([
  "add", "build", "create", "make", "implement", "let", "allow", "support", "enable", "fix", "change", "improve",
  "remove", "replace", "integrate", "design", "write", "generate", "refactor", "migrate", "automate",
  "תוסיף", "תוסיפו", "הוסף", "הוסיפו", "תבנה", "תבנו", "בנה", "בנו", "תיצור", "צור", "תעשה", "עשה", "תממש", "ממש", "תאפשר", "אפשר",
  "תתקן", "תקן", "שפר", "תשפר", "תחליף", "החלף", "תסיר", "הסר",
]);

// Politeness in front of a request: "can you add X", "please build X", "תוכל להוסיף X".
const POLITE = new Set(["please", "pls", "can", "could", "would", "will", "you", "we", "תוכל", "תוכלו", "בבקשה", "נא", "אנא"]);

// Phrases that open an idea even though they start with a pronoun or a question word.
const IDEA_PHRASES = [
  /^(i|we) (want|need|would like|wish)\b/,
  /^(it would be|would be) (nice|great|good)\b/,
  /^(אני|אנחנו|אנו) (רוצה|רוצים|צריך|צריכים|מבקש|מבקשים)(?=\s|$)/,
  /^(כדאי|חסר)(?=\s|$)/,
];
const QUESTION_PHRASES = [REQUEST_FOR_INFO, /\bany news\b/, /\bwhere (is|are)\b/, /\bwhat('s| is| are| happened)\b/, /^תעדכן/, /^(תראה|תן|ספר|תספר) לי/, /מה (קורה|המצב|עשינו|נעשה)/];
// "I want to know...", "אני רוצה לדעת...": the want-phrase is wrapped around a request for information.
const WANTS_INFO = /\b(know|see|understand|find out|an update|the status)\b|לדעת|לראות|להבין|עדכון|סטטוס/;

function tokens(text: string): string[] {
  return text
    .replace(/['’"׳״]/g, "")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

// A follow-up in a conversation often opens with a conjunction ("And who is working on it?", "וגם מי עובד על זה?"):
// it carries no signal of its own, so the word after it is read as the opener.
const LEADING_CONJUNCTIONS = new Set(["and", "also", "but", "so", "then", "plus", "ok", "okay", "וגם", "גם", "אז", "אבל", "טוב", "אוקיי"]);

/** Drops the leading conjunctions ("ok so, and who…") so every check below, words and phrases alike, sees the real opener. */
function stripLeadingConjunctions(text: string): string {
  let t = text;
  for (;;) {
    const ws = tokens(t);
    if (ws.length < 2 || !LEADING_CONJUNCTIONS.has(ws[0]!)) return t;
    t = t.replace(/^[^\p{L}\p{N}]*[\p{L}\p{N}'’]+[^\p{L}\p{N}]*/u, "").trim();
  }
}

export function classifyHomeBoxText(raw: string): HomeBoxClassification {
  const text = stripLeadingConjunctions(raw.trim().toLowerCase().replace(/\s+/g, " "));
  const words = tokens(text);
  if (words.length === 0) return { kind: "unclear", reason: "empty" };

  // "Can you add X" / "please build X": skip the politeness, then look for the verb.
  let i = 0;
  while (i < words.length - 1 && i < 3 && POLITE.has(words[i]!)) i++;
  const politeLead = i > 0;
  const lead = words[i]!;

  const wantsInfo = WANTS_INFO.test(text);
  const ideaPhrase = IDEA_PHRASES.some((p) => p.test(text)) && !wantsInfo;
  const idea = ideaPhrase || (IDEA_STARTS.has(lead) && (politeLead || !QUESTION_STARTS.has(words[0]!)));
  const endsWithQuestionMark = /[?？]\s*$/.test(text);
  const hasQuestionWord = QUESTION_STARTS.has(words[0]!) || QUESTION_PHRASES.some((p) => p.test(text)) || (IDEA_PHRASES.some((p) => p.test(text)) && wantsInfo);
  const question = !idea && hasQuestionWord;
  // A bare "Export to CSV?" has a question mark but no question: ask rather than guess.
  if (!idea && !hasQuestionWord && endsWithQuestionMark) return { kind: "unclear", reason: "question mark without a question" };

  // "Add export?" is a request wearing a question mark: not clear-cut, so ask the person.
  if (idea && endsWithQuestionMark && !politeLead && !ideaPhrase) return { kind: "unclear", reason: "build verb with a question mark" };
  if (idea) return { kind: "idea", reason: "starts like a piece of work" };
  if (question) return { kind: "question", reason: "asks for information" };
  // A longer description with neither signal reads as a feature idea; a short fragment is a guess.
  if (words.length >= 5) return { kind: "idea", reason: "describes something without asking" };
  return { kind: "unclear", reason: "too short to tell" };
}
